/**
 * Form draft persistence helper.
 *
 * Any long, in-progress form (Bill of Lading, Signed Estimate, booking funnel step)
 * mirrors its state to localStorage. If the browser crashes, the tab reloads, the
 * iPad's Safari tab is evicted, or the network drops before a DB autosave completes,
 * we can restore the user's work.
 *
 * Drafts are keyed by feature + record ID and expire after 7 days.
 */

const PREFIX = 'lm-draft:';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

interface Envelope<T> {
  savedAt: number;
  data: T;
}

function key(scope: string, recordId: string) {
  return `${PREFIX}${scope}:${recordId}`;
}

export function saveDraft<T>(scope: string, recordId: string, data: T): void {
  try {
    const env: Envelope<T> = { savedAt: Date.now(), data };
    localStorage.setItem(key(scope, recordId), JSON.stringify(env));
  } catch {
    // Quota exceeded / private mode — silently fail; DB autosave is still the source of truth.
  }
}

export function loadDraft<T>(scope: string, recordId: string): { data: T; savedAt: Date } | null {
  try {
    const raw = localStorage.getItem(key(scope, recordId));
    if (!raw) return null;
    const env = JSON.parse(raw) as Envelope<T>;
    if (!env?.savedAt || Date.now() - env.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(key(scope, recordId));
      return null;
    }
    return { data: env.data, savedAt: new Date(env.savedAt) };
  } catch {
    return null;
  }
}

export function clearDraft(scope: string, recordId: string): void {
  try {
    localStorage.removeItem(key(scope, recordId));
  } catch {
    /* ignore */
  }
}

/**
 * Sweep old drafts on app boot so localStorage doesn't grow forever.
 * Called from App.tsx at mount time.
 */
export function sweepStaleDrafts(): void {
  try {
    const now = Date.now();
    const toDelete: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(PREFIX)) continue;
      try {
        const env = JSON.parse(localStorage.getItem(k) ?? 'null') as Envelope<unknown> | null;
        if (!env?.savedAt || now - env.savedAt > MAX_AGE_MS) toDelete.push(k);
      } catch {
        toDelete.push(k);
      }
    }
    toDelete.forEach(k => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}
